import { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';
import { documentosApi, prestacoesApi, unidadesApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import type { Unidade } from '../types';
import * as XLSX from 'xlsx';
import { GeneratedReportFile } from '../constants/types';
import { normalizeReportData } from '../helpers/normalize-report-data';
import { toCSV } from '../helpers/toCSV';
import { downloadReportFile } from '../helpers/download-report-file';
import ExportButton from '../components/ExportButton';
import { add, format } from 'date-fns';

type ReportFormatType = 'csv' | 'xlsx' | 'pdf'
type ReportDataType = 'prestacoes' | 'documentos' | 'financeiro' | 'unidades'

const reportTypes: Array<{label: string, value: ReportDataType}> = [
  {
    label: "Prestações de Contas",
    value: "prestacoes"
  },
  {
    label: "Documentos Fiscais",
    value: "documentos"
  },
  {
    label: "Resumo Financeiro",
    value: "financeiro"
  },
  {
    label: "Por Unidade",
    value: "unidades"
  }
]

const applyReportFormat = (
  format: ReportFormatType,
  dataType: ReportDataType,
  data: any
): GeneratedReportFile | null => {
  const rows = normalizeReportData(data);

  if ((format === 'csv' || format === 'xlsx') && rows.length === 0) {
    alert('Nenhum dado encontrado para o período selecionado.');
    return null;
  }

  const baseFileName = `relatorio-${dataType}-${new Date().toISOString().slice(0, 10)}`;

  switch (format) {
    case 'pdf':
      alert('Formato PDF ainda não disponível');
      return null;

    case 'csv': {
      const content = toCSV(rows);
      return {
        content,
        mimeType: 'text/csv;charset=utf-8;',
        extension: 'csv',
        suggestedFileName: `${baseFileName}.csv`,
      };
    }

    case 'xlsx': {
      const wb = XLSX.utils.book_new();

      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws);

      if (wb.SheetNames.length === 0) {
        alert('Nenhum dado encontrado para o período selecionado.');
        return null;
      }

      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
      });

      return {
        content: wbout,
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extension: 'xlsx',
        suggestedFileName: `${baseFileName}.xlsx`,
      };
    }

    default:
      return null;
  }
}

const createYearRange = (start: number, end: number): number[] => {
  const range: number[] = [];
  if (Number.isNaN(start) || Number.isNaN(end)) return range;

  const step = start <= end ? 1 : -1;
  for (let year = start; step > 0 ? year <= end : year >= end; year += step) {
    range.push(year);
  }

  return range;
}

export function RelatoriosPage() {
  const _dateStart = format(add(new Date(), { years: -1 }), 'yyyy-MM-dd')
  const _dateEnd = format(new Date(), 'yyyy-MM-dd')

  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroRelatorio, setFiltroRelatorio] = useState<ReportDataType>(reportTypes[0].value);
  const [filtroDataInicio, setFiltroDataInicio] = useState(_dateStart);
  const [filtroDataFim, setFiltroDataFim] = useState(_dateEnd);
  const [filtroUnidade, setFiltroUnidade] = useState<number | undefined>();
  const { selectedUnidadeId } = useAuthStore();

  useEffect(() => {
    // Se o usuário selecionou uma unidade específica, filtrar automaticamente
    if (selectedUnidadeId !== null && selectedUnidadeId !== undefined) {
      setFiltroUnidade(selectedUnidadeId);
    }
  }, [selectedUnidadeId]);

  useEffect(() => {
    loadData();
  }, [filtroUnidade]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [unidadesData] = await Promise.all([
        unidadesApi.list(),
      ]);
      setUnidades(unidadesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: ReportFormatType) => {
    const report = filtroRelatorio

    const loadReportData = async () => {
      switch(report) {
        case 'prestacoes':
          return await prestacoesApi.listInterval(filtroDataInicio, filtroDataFim, filtroUnidade);
        case 'documentos':
          return await documentosApi.listInterval(filtroDataInicio, filtroDataFim, filtroUnidade);
        case 'financeiro':
          // TODO: implementar quando API estiver pronta
          return [];
        case 'unidades':
          // TODO: implementar quando API estiver pronta
          return [];
        default:
          return [];
      }
    }

    const data = await loadReportData();
    const file = applyReportFormat(format, report, data);

    if (!file) return;

    downloadReportFile(file);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <BackButton to="/dashboard" />
        </div>

        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Relatórios Personalizados
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Gerencie e acompanhe suas prestações de contas
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Configuração do Relatório
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Relatório
              </label>
              <select
                value={filtroRelatorio}
                onChange={(e) => setFiltroRelatorio(e.target.value as ReportDataType)}
                className="w-full min-h-[42px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {reportTypes.map(({ label, value }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Unidade
              </label>
              <select
                value={filtroUnidade || ''}
                onChange={(e) => setFiltroUnidade(e.target.value ? Number(e.target.value) : undefined)}
                disabled={selectedUnidadeId !== null && selectedUnidadeId !== undefined}
                className="w-full min-h-[42px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Todas as unidades</option>
                {unidades.map((unidade) => (
                  <option key={unidade.id} value={unidade.id}>
                    {unidade.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Início
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                value={filtroDataInicio}
                onChange={(e) => setFiltroDataInicio(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Fim
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                value={filtroDataFim}
                onChange={(e) => setFiltroDataFim(e.target.value)}
              />
            </div>
          </div>
        </div>


        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Exportar Relatório
          </h2>
          <div className="flex gap-4 mb-4">
            <ExportButton
              title='Exportar CSV'
              color='blue'
              onClick={() => exportReport('csv')}
            />

            <ExportButton
              title='Exportar Excel'
              color='green'
              onClick={() => exportReport('xlsx')}
            />

            <ExportButton
              title='Exportar PDF'
              color='red'
              onClick={() => exportReport('pdf')}
            />
          </div>
        </div>

        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
            Sobre os Relatórios
          </h3>
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            <li>
              <strong>Prestações de Contas:</strong>
              <span> Detalhamento de todas as prestações por período</span>
            </li>
            <li>
              <strong>Documentos Fiscais:</strong>
              <span> Lista completa de documentos com valores e status</span>
            </li>
            <li>
              <strong>Resumo Financeiro:</strong>
              <span> Análise financeira por unidade e período</span>
            </li>
            <li>
              <strong>Por Unidade:</strong>
              <span> Consolidado de atividades por unidade</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
}
